// ContactTableRedesign.tsx — Kontakte-Tabelle im „Refined Classic"-Redesign,
// an die echte API angebunden.
//
// Features (über die generische DataTable-Engine + Hooks):
//  - Infinite-Scroll (offset/limit, GET /contact, X-Total-Count) via useInfiniteRows
//  - Default-Sortierung alphabetisch (Backend), per-Spalte server-seitig sortierbar
//  - per-Spalte Filter (immer sichtbares Icon), Backend-Filter-Keys unverändert
//  - Spalten verschieben / aus-/einblenden / schmal zusammenklappen (persistiert)
//  - fixer Header (Sticky am Scroll-Container)
//  - Bottom-Leiste (nur bei Auswahl/Filter) mit Zählern + Bulk-Löschen (admin/gfgl)

import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { I, FirmaIcon, PersonIcon } from "./kvpIcons";
import { STATUS_MIT_EIGENEM_CHIP } from "./table/sonstigeFilterModel";
import { KStatusFilter, KStatusSheetFeld } from "./table/KStatusFilter";
import {
  ArbeitsZusatzZeile,
  KartenAbschnitte,
  UmfeldRolleZeile,
  OffenePunkteBadges,
  type ArbeitsZusatz,
} from "./table/kontaktKarten";
import { kStatusFilterPayload, kStatusWahlAusFilterWert, wahlBeschreibung } from "./kontaktstatusWahl";
import {
  K_STATUS_SPALTE,
  anfangsFilter,
  effektiveFilterWerte,
  nurUserFilter,
  standardAktiv,
  standardFilterWerte,
  weichtVomStandardAb,
  zaehlbareKontaktFilter,
  type FilterWerte,
  type KStatusQuelle,
} from "./kontaktStandardFilter";
import { useOhneFilterProbe } from "../../hooks/useOhneFilterProbe";
import { OhneFilterHinweis } from "./table/OhneFilterHinweis";
import {
  VORGANG_FILTER_OPTIONEN,
  VORGANG_SPALTE,
  VORGANG_SPALTEN_FILTER,
  mitVorgangFilterWert,
  vorgangFilterEingerueckt,
  vorgangFilterLabel,
  vorgangFilterWert,
} from "./table/vorgangFilter";
import {
  abschnitteLesen,
  abschnitteSpeichern,
  alterAm,
  arbeitsansichtLesen,
  arbeitsansichtSpeichern,
  hatOffenePunkte,
  mailZeitLabel,
  nachMailZeit,
  type AbschnittKey,
  type AbschnitteOffen,
} from "./table/arbeitsansicht";
import {
  DataTable,
  ColumnsMenu,
  type Column,
  type DataTableAbschnitt,
  type SortState,
  type BoFoOption,
  Checkbox,
  Chip,
  Dash,
  RdPopover,
  StarRating,
  TableBottomBar,
  TypeCell,
  VorgangBadge,
} from "./table";
import { FoBoBadge, foBoLabels } from "../shared/foBo";
import "./contactRedesign.css";
import { KontaktmoeglichkeitCell } from "./KontaktmoeglichkeitPopup";
import { KundenverbindungCell } from "./KundenverbindungPopup";
import { OffeneVorgaengeDialog } from "../OffeneVorgaengeDialog";
import { apiFetch } from "../../utils/apiFetch";
import { fetchTeamsLite } from "../../utils/teamsLite";
import { defaultContactScope } from "../../utils/contactScope";
import { mapContactRow } from "../../utils/contactRowMapper";
import { useVerpassteAnrufe, verpasstAbschnitt } from "./table/verpassteAnrufe";
import {
  CO_ART_SPALTE,
  CO_SPALTEN,
  initialeSichtbarkeit,
  variantenKonfiguration,
  type TabellenVariante,
} from "./table/contactTableVarianten";
import {
  CO_ARTEN,
  CO_HALBJAHRE,
  CO_KATEGORIEN,
  coArtenDerZeile,
  halbjahrLabel,
} from "../../data/coInternKatalog";
import { CoAktiv, CoLink, CoLogo, CoMehrzeilig, CoNote } from "./table/coInternZellen";
import { contactTypeLabel } from "../../utils/contactTypeLabel";
import {
  VERSTORBEN_ZEICHEN,
  istVerstorben,
  verstorbenTitel,
} from "../../utils/contactDisplay";
import { useDebouncedValue } from "../../utils/useDebouncedValue";
import { useInfiniteRows } from "../../hooks/useInfiniteRows";
import { sollAufAlleWechseln } from "./kontaktsucheScope";
import { useResponsive } from "../../hooks/useResponsive";
import { useAuth } from "../../context/AuthContext";
import { anlagePfadAusSuche } from "../../utils/nameAusSuche";
import { listAppUsers } from "../../services/userService";
import { cached } from "../../utils/swrCache";
import type { ContactScope } from "../contacts/ContactScopeToggle";
import { trefferKandidat, type TrefferKandidat } from "./table/unternehmensverbund";
import { trefferUndUmfeld, type UmfeldRolle } from "./table/trefferUmfeld";
import { useTrefferUmfeld } from "./table/useUnternehmensverbund";
import {
  getCurrentUserPreferenceKey,
  fetchUserPreferences,
  saveUserPreference,
} from "../../utils/userPreferences";
import { useTableNavState } from "../../hooks/useTableNavState";
import type { ContactDetailsFormValues } from "../../types/Contact";
import { mapsUrl } from "../../utils/mapsUrl";
import { hinweisen } from "./bestaetigen";

/**
 * Eine Tabellenzeile. `arbeitsZusatz` trägt nur die Arbeitsansicht: die Zeile
 * unter dem Namen („wird heute 54", Uhrzeit + Betreff der neuen Mail).
 */
type Row = ContactDetailsFormValues & {
  arbeitsZusatz?: ArbeitsZusatz;
  /** Nur Zeilen im Umfeld-Abschnitt (Verbund/Haushalt des Treffers): die Rolle. */
  umfeldRolle?: UmfeldRolle;
};

/** Persistierter Tabellen-Zustand für den Detail-Rücksprung. */
type TableSnapshot = {
  search: string;
  filterValues: Record<string, string | boolean | undefined>;
  /**
   * Woher der K-Status-Filter stammt (Standard oder User, 02.10.2026). Fehlt
   * in Snapshots von vorher — dann gilt er als vom User gesetzt.
   */
  kStatusQuelle?: KStatusQuelle;
  boFoValue: { boTeamIds: string[]; foUserIds: string[] };
  sort: SortState | null;
  scope: ContactScope;
  scrollTop: number;
  loadedCount: number;
};

const STATUS_TONE: Record<string, string> = {
  Kunde: "chip-kunde",
  Interessent: "chip-interessent",
  Sonstige: "chip-sonstige",
};

// Kontaktstatus: alles außer Kunde/Interessent wird als "Sonstige" angezeigt.
// Klick öffnet ein Popover (kein Fenster) mit dem echten Status (z. B. "Unbekannt").
function StatusPopover({ status }: { status: string }) {
  return (
    <RdPopover
      align="start"
      width={220}
      trigger={({ open, toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          className={"chip chip-sonstige" + (open ? " on" : "")}
          title={`Status: ${status}`}
          onClick={(e) => { e.stopPropagation(); toggle(); }}
          style={{ cursor: "pointer" }}
        >
          <span className="cd" />Sonstige
        </button>
      )}
    >
      {() => (
        <div className="rd-pop-body" onClick={(e) => e.stopPropagation()}>
          <div className="rd-pop-grouptitle">Kontaktstatus</div>
          <div style={{ padding: "6px 10px", fontSize: 13, color: "#241522" }}>{status}</div>
        </div>
      )}
    </RdPopover>
  );
}

// Die reorder-fähigen (nicht-pinned) Spalten-Ids in Default-Reihenfolge stehen
// je Variante in `contactTableVarianten.ts` — „CO intern" hat eine Spalte mehr.
// Typ + Name sind pinned/eingefroren (führend, nicht verschiebbar) und stehen
// deshalb in keiner der beiden Listen.

const FunnelIcon = (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M2.5 4h13l-5 6v4l-3 1.5V10z" />
  </svg>
);
// Google-Maps-Pin (Google-Grün), gefüllt mit weißem Zentrum.
const MapPinIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#34A853" aria-hidden="true">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.6" fill="#fff" />
  </svg>
);

// Symbole der Abschnittsköpfe in der Arbeitsansicht.
const ICON_TORTE = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.5 17h13v-6a1.5 1.5 0 0 0-1.5-1.5H5A1.5 1.5 0 0 0 3.5 11z" />
    <path d="M3.5 13.5c1.2.8 2.4.8 3.6 0s2.4-.8 3.6 0 2.4.8 3.6 0 1.6-.6 2.2-.4" />
    <path d="M10 9.5V6.5" />
    <path d="M10 6.5c-.8 0-1.2-.8-.8-1.6L10 3l.8 1.9c.4.8 0 1.6-.8 1.6z" />
  </svg>
);
const ICON_MAIL = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
    <path d="M3 6l7 5 7-5" />
  </svg>
);
const ICON_TELEFON = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5.5 3h2.2l1.1 3.6-1.6 1.2a9 9 0 0 0 5 5l1.2-1.6 3.6 1.1v2.2A1.8 1.8 0 0 1 15.2 16 12.4 12.4 0 0 1 4 4.8 1.8 1.8 0 0 1 5.5 3z" />
    <path d="M13 3l4 4M17 3l-4 4" />
  </svg>
);
const ICON_MENSCHEN = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="7.5" cy="7" r="3" />
    <path d="M2 17c.5-3 2.7-4.8 5.5-4.8S12.5 14 13 17" />
    <path d="M13 4.2a3 3 0 0 1 0 5.6M15 12.6c1.6.6 2.7 2.2 3 4.4" />
  </svg>
);

/** Wird das Land in der Liste gezeigt? Nur, wenn es etwas Neues sagt. */
function auslandsHinweis(land: string | undefined): string {
  const l = (land ?? "").trim();
  return !l || l.toLowerCase() === "deutschland" ? "" : l;
}

/**
 * Prüft eine gespeicherte Spaltenreihenfolge gegen die Reihenfolge DIESER
 * Variante.
 *
 * Die Länge muss stimmen: Eine Reihenfolge aus der anderen Variante hätte eine
 * Spalte zu viel oder zu wenig und würde die Tabelle entweder verstümmeln oder
 * eine Spalte doppelt führen. Weil beide Varianten eigene Preference-Scopes
 * haben, kann das nur bei einem Altstand aus der Zeit vor „CO intern"
 * passieren — der wird dann verworfen und die Default-Reihenfolge greift.
 */
const istGueltigeReihenfolge = (wert: unknown, erlaubt: string[]): wert is string[] =>
  Array.isArray(wert) &&
  wert.length === erlaubt.length &&
  wert.every((id) => typeof id === "string" && erlaubt.includes(id));

export type ContactTableProps = {
  /**
   * Welche der beiden Listen gezeigt wird. Ohne Angabe die gewöhnliche
   * Kontaktliste — jeder bestehende Aufruf bleibt damit unverändert.
   */
  variante?: TabellenVariante;
};

const ContactTableRedesign: React.FC<ContactTableProps> = ({ variante = "kontakte" }) => {
  // Alle Unterschiede zwischen „Kontakte" und „CO intern" hängen an diesem
  // einen Objekt — verstreute `variante === "cointern"`-Abfragen im JSX wären
  // in einer Komponente dieser Größe nicht mehr überblickbar.
  const konf = variantenKonfiguration(variante);
  const navigate = useNavigate();
  const { isAdmin, isGfgl, teams, canTempDeleteContacts, hasPermission } = useAuth();
  // Löschen (Soft-Delete → Papierkorb): admin/gfgl plus die temporäre app-weite
  // Lösch-Allowlist (CLL/SAH). Das Backend erzwingt es zusätzlich pro Zeile.
  const canDelete = isAdmin || isGfgl || canTempDeleteContacts;

  // Phone (≤768) → Karten-Liste statt Tabelle; Filter über ein Sheet
  // (auf Karten gibt es keine Spaltenköpfe). Tablet/Desktop bleiben Tabelle.
  const { isPhone } = useResponsive();
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Zustand beim Detail-Rücksprung wiederherstellen (sonst frischer Start).
  const { initial: restored, save: saveNavState } = useTableNavState<TableSnapshot>(konf.navStateKey, {
    isDetailRoute: (p) => /^\/contacts\/\d+(\/|$)/.test(p) || /^\/kontakte\/edit\/\d+/.test(p),
  });
  const scrollRef = useRef<HTMLDivElement>(null);

  // --- Such-/Filter-/Sort-State (ggf. aus Snapshot vorbelegt) ---
  const [search, setSearch] = useState(restored?.search ?? "");
  const debouncedSearch = useDebouncedValue(search, 300);
  // Standardfilter „Kunde + Interessent" (User 01.10.2026) beim frischen
  // Einstieg; beim Rücksprung der Snapshot samt Quelle. Regel und Begründung:
  // `kontaktStandardFilter.ts`.
  const [anfang] = useState(() => anfangsFilter(variante, restored));
  // T-284: ein Tabellenstand von vor dem Art-Filter trägt `Vorgange: true` —
  // die Auswahl kennt dafür den String „true" (= Alle offenen Vorgänge).
  const [filterValues, setFilterValues] = useState<FilterWerte>(() => mitVorgangFilterWert(anfang.filterValues));
  const [kStatusQuelle, setKStatusQuelle] = useState<KStatusQuelle>(anfang.kStatusQuelle);
  // Arbeitsansicht (User 28.09.2026) — ersetzt den früheren Vorfilter
  // „Sonstige Kontakte". Nur in der gewöhnlichen Kontaktliste.
  const [arbeitsansicht, setArbeitsansicht] = useState<boolean>(
    () => konf.zeigtSonstigeVorfilter && arbeitsansichtLesen(),
  );
  const [abschnitteOffen, setAbschnitteOffen] = useState<AbschnitteOffen>(abschnitteLesen);
  const abschnittUmschalten = (k: AbschnittKey) =>
    setAbschnitteOffen((alt) => {
      const neu = { ...alt, [k]: !alt[k] };
      abschnitteSpeichern(neu);
      return neu;
    });
  const [boFoValue, setBoFoValue] = useState<{ boTeamIds: string[]; foUserIds: string[] }>(restored?.boFoValue ?? { boTeamIds: [], foUserIds: [] });
  const [sort, setSort] = useState<SortState | null>(restored?.sort ?? null);

  // Die Ansicht „Kundenverbindungen" ist seit 28.09.2026 eine Karte im
  // Dashboard. Alte Links (`?view=kundenverbindungen`, z. B. nach einem
  // KV-Merge) führen deshalb dorthin.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (konf.zeigtKundenverbindungen && searchParams.get("view") === "kundenverbindungen") {
      navigate("/?kv=1", { replace: true });
    }
  }, [searchParams, konf.zeigtKundenverbindungen, navigate]);
  // Raster (volle Gitterlinien h+v) ein/aus.
  const [gridOn, setGridOn] = useState(false);

  // --- RBAC-Scope (Alle / Team / Persönlich) ---
  // Default „Team" für alle außer admin (A18); ohne Team → „Alle". Ein per
  // Nav-Restore wiederhergestellter Scope (Detail-Rücksprung) hat Vorrang.
  // `teams`/`isAdmin` sind hier final: RequireAuth rendert die Seite erst,
  // wenn die RBAC-Permissions (inkl. Teams) aufgelöst sind.
  const hasTeam = (teams?.length ?? 0) > 0;
  const [scope, setScope] = useState<ContactScope>(
    () => restored?.scope ?? defaultContactScope(isAdmin, hasTeam),
  );
  // Team-Scope gewählt, aber kein Team (mehr) → zurück auf 'all'.
  useEffect(() => {
    if (scope === "team" && !hasTeam) setScope("all");
  }, [scope, hasTeam]);

  // "Alle" und "Persönlich" immer; "Team" nur, wenn der User mindestens ein
  // Team hat (Regel: kein Team → kein Team-Eintrag). Für alle Rollen sichtbar.
  const scopeOptions = useMemo(() => {
    const opts: { value: ContactScope; label: string }[] = [{ value: "all", label: "Alle" }];
    if (hasTeam) opts.push({ value: "team", label: "Team" });
    opts.push({ value: "own", label: "Persönlich" });
    return opts;
  }, [hasTeam]);

  // --- Auswahl ---
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Offene-Vorgänge-Dialog (Klick auf den Vorgänge-Badge)
  const [vorgaengeFor, setVorgaengeFor] = useState<{ id: number; name: string } | null>(null);

  // --- Spalten: Reihenfolge / Sichtbarkeit / Collapse ---
  const [columnOrder, setColumnOrder] = useState<string[]>(konf.spaltenReihenfolge);
  // Anfangszustand aus der Variante: „CO intern" öffnet ohne die Spalten der
  // Kundenliste, die dort ins Leere laufen (K-Relevanz, K-Wert …). Ein Lazy-
  // Initializer, weil die Komponente je Route mit eigenem `key` montiert wird
  // — ein Variantenwechsel ohne Neumontage kommt nicht vor.
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() =>
    initialeSichtbarkeit(konf),
  );
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [collapsedColumns, setCollapsedColumns] = useState<string[]>([]);

  // --- Filter-Optionen (BO-Teams / FO-User) ---
  const [teamOptions, setTeamOptions] = useState<BoFoOption[]>([]);
  const [userOptions, setUserOptions] = useState<BoFoOption[]>([]);

  // Mitgliederzahl je Kundenverbindung (für den "·N"-Zähler der Spalte) —
  // einmal laden + cachen, nicht pro Zeile.
  const [kvCounts, setKvCounts] = useState<Record<number, number>>({});
  useEffect(() => {
    let cancelled = false;
    cached<{ id: number; name: string; memberCount: number }[]>(
      "contactgroup.kundenverbindungen",
      async () => {
        const { data } = await apiFetch<{ id: number; name: string; memberCount: number }[]>("contactgroup/kundenverbindungen");
        return Array.isArray(data) ? data : [];
      },
      {
        ttlMs: 5 * 60_000,
        onUpdate: (fresh) => {
          if (!cancelled) setKvCounts(Object.fromEntries(fresh.map((g) => [g.id, g.memberCount])));
        },
      },
    )
      .then((list) => {
        if (!cancelled) setKvCounts(Object.fromEntries(list.map((g) => [g.id, g.memberCount])));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ---- Daten (Infinite-Scroll) ----
  // Solange der Standard unangetastet ist, durchsucht eine Suche alle Status
  // (auch Sonstige) — der K-Status-Anteil fällt dann weg.
  const { werte: effektiveWerte } = effektiveFilterWerte({
    filterValues,
    kStatusQuelle,
    suchbegriff: debouncedSearch,
  });
  const filterObjekt = useCallback(
    (werte: FilterWerte): string => {
      const f: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(werte)) {
        if (v === undefined || v === "" || v === false) continue;
        if (k === K_STATUS_SPALTE) {
          // Übersetzt Hauptstatus (Mehrfachauswahl) und Rollen in ihre
          // Backend-Schlüssel; Altwerte laufen wie bisher.
          Object.assign(f, kStatusFilterPayload(v));
          continue;
        }
        f[k] = v;
      }
      if (boFoValue.boTeamIds.length) f.boTeamIds = boFoValue.boTeamIds;
      if (boFoValue.foUserIds.length) f.foUserIds = boFoValue.foUserIds;
      // Der feste Filter der Variante steht ZULETZT: Trüge ein Spaltenfilter
      // denselben Schlüssel, gewänne sonst der zuletzt geschriebene Wert — stumm,
      // und die Liste zeigte etwas anderes als ihr Name behauptet.
      Object.assign(f, konf.festerFilter);
      return JSON.stringify(f);
    },
    [boFoValue, konf],
  );
  const effektiveWerteKey = JSON.stringify(effektiveWerte);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filtersJson = useMemo(() => filterObjekt(effektiveWerte), [filterObjekt, effektiveWerteKey]);
  // Arbeitsansicht-Abschnitte (Geburtstag, Neue Mail): nur die Filter des
  // Users — der Standard blendet dort keine Sonstigen aus.
  const userFilterKey = JSON.stringify(nurUserFilter(filterValues, kStatusQuelle));
  const arbeitFiltersJson = useMemo(
    () => filterObjekt(JSON.parse(userFilterKey) as FilterWerte),
    [filterObjekt, userFilterKey],
  );

  // Arbeitsansicht: ohne eigene Spaltensortierung sortiert der Server nach
  // offenen Punkten (Vorgang > Termin > Schaden > Zahlungsverkehr), dann A–Z.
  const arbeitsSortierung = arbeitsansicht && !sort;
  const effektiveSortierung: SortState | null = arbeitsSortierung
    ? { field: "Arbeitsansicht", direction: "asc" }
    : sort;
  const includeTokens = arbeitsansicht ? [konf.includeTokens, "arbeit"].filter(Boolean).join(",") : konf.includeTokens;

  const { rows, total, hasMore, loading, loadMore, removeByKeys } = useInfiniteRows<Row>({
    getKey: (r) => r.kontaktId,
    deps: [debouncedSearch, filtersJson, effektiveSortierung?.field ?? "", effektiveSortierung?.direction ?? "", scope, includeTokens],
    // Beim Wiederherstellen die zuvor geladene Zeilenanzahl in EINEM Request laden
    // (gedeckelt), damit die Scroll-Position wieder erreichbar ist.
    initialOffset: restored ? Math.min(restored.loadedCount, 300) : undefined,
    fetchPage: async ({ limit, offset, signal }) => {
      const { data, total: t } = await apiFetch<any[]>("contact", {
        signal,
        params: {
          limit,
          offset,
          search: debouncedSearch,
          filters: filtersJson,
          scope,
          include: includeTokens,
          sort: effektiveSortierung?.field ?? "",
          direction: effektiveSortierung?.direction ?? "",
        },
      });
      return { rows: (Array.isArray(data) ? data : []).map(mapContactRow), total: t };
    },
  });

  // ---- Umfeld des Treffers als Tabellenzeilen ----
  // Trifft die Suche eindeutig eine Firma bzw. Person, hängen ihr
  // Unternehmensverbund bzw. Haushalt als eigener Abschnitt hinter den
  // Treffern (User 28.09.2026; vorher eine Karte unter der Tabelle). Die
  // Zeilen kommen aus derselben Liste (`filters.Ids`), also mit Mandant und
  // Sichtbarkeit wie die Treffer; jeder Kontakt steht nur einmal da — im
  // Umfeld, mit seiner Rolle.
  //
  // Während „Mehr laden" / Umsortieren bei gleichem Suchbegriff bleibt der
  // bisherige Kandidat stehen — sonst verschwände der Abschnitt bei jedem
  // Nachladen und käme neu geladen wieder.
  const kandidatJetzt = trefferKandidat({
    suchbegriff: debouncedSearch,
    rows,
    total,
    hasMore,
    loading,
    relevanzSortiert: !effektiveSortierung,
  });
  const letzterKandidat = useRef<{ such: string; k: TrefferKandidat | null }>({ such: "", k: null });
  let umfeldKandidat = kandidatJetzt;
  if (loading && letzterKandidat.current.such === debouncedSearch) umfeldKandidat = letzterKandidat.current.k;
  else letzterKandidat.current = { such: debouncedSearch, k: kandidatJetzt };
  const umfeldFesterFilter = JSON.stringify(konf.festerFilter ?? {});
  const umfeldDaten = useTrefferUmfeld<Row>(
    umfeldKandidat,
    async (ids, signal) => {
      const { data } = await apiFetch<any[]>("contact", {
        signal,
        params: {
          limit: Math.min(ids.length, 500),
          offset: 0,
          search: "",
          // Der feste Filter der Variante (z. B. „CO intern") gilt auch hier;
          // die Spaltenfilter der Treffer nicht — das Umfeld ist kein Treffer.
          filters: JSON.stringify({ ...JSON.parse(umfeldFesterFilter), Ids: ids }),
          scope: "all",
          include: includeTokens,
          sort: "",
          direction: "",
        },
      });
      return (Array.isArray(data) ? data : []).map(mapContactRow);
    },
    `${includeTokens}|${umfeldFesterFilter}`,
  );
  const umfeld = useMemo(
    () => trefferUndUmfeld(rows, umfeldDaten.plan, umfeldDaten.zeilen),
    [rows, umfeldDaten],
  );
  /** Die Treffer ohne die Kontakte, die im Umfeld-Abschnitt stehen. */
  const trefferRows = umfeld.treffer;
  /** Alles, was die Tabelle zeigt — Auswahl und Vor/Zurück in der Akte. */
  const angezeigteZeilen: Row[] = useMemo(
    () => (umfeld.gruppen.length ? [...trefferRows, ...umfeld.gruppen.flatMap((g) => g.zeilen)] : rows),
    [umfeld, trefferRows, rows],
  );

  // ---- Arbeitsansicht: die beiden oberen Abschnitte + Zähler ----
  // Gewöhnliche Listenabfragen mit dem Filter `Arbeit` — Suche, Spaltenfilter,
  // Bereich und Sichtbarkeit gelten damit genauso wie für „Alle Kontakte".
  const [geburtstage, setGeburtstage] = useState<Row[]>([]);
  const [neueMails, setNeueMails] = useState<Row[]>([]);
  const [offenAnzahl, setOffenAnzahl] = useState<number | null>(null);
  useEffect(() => {
    if (!arbeitsansicht) {
      setGeburtstage([]);
      setNeueMails([]);
      setOffenAnzahl(null);
      return;
    }
    const ctrl = new AbortController();
    // Geburtstag und Neue Mail: nur die Filter des Users (der Standard
    // „Kunde + Interessent" gilt dort nicht). Die Zahl „offen" gehört zur
    // Liste „Alle Kontakte" darunter und zählt mit deren Filtern.
    const mitFilter = (zusatz: Record<string, unknown>, basis: string) =>
      JSON.stringify({ ...JSON.parse(basis), ...zusatz });
    const laden = (arbeit: string, limit: number, sortierung: string, basis = arbeitFiltersJson) =>
      apiFetch<any[]>("contact", {
        signal: ctrl.signal,
        params: {
          limit,
          offset: 0,
          search: debouncedSearch,
          filters: mitFilter({ Arbeit: arbeit }, basis),
          scope,
          include: includeTokens,
          sort: sortierung,
          direction: "asc",
        },
      });
    laden("geburtstag", 100, "Name")
      .then(({ data }) => setGeburtstage((Array.isArray(data) ? data : []).map(mapContactRow)))
      .catch(() => {});
    laden("neueMail", 100, "Name")
      .then(({ data }) => setNeueMails(nachMailZeit((Array.isArray(data) ? data : []).map(mapContactRow))))
      .catch(() => {});
    laden("offen", 1, "", filtersJson)
      .then(({ total: t }) => setOffenAnzahl(typeof t === "number" ? t : null))
      .catch(() => {});
    return () => ctrl.abort();
  }, [arbeitsansicht, debouncedSearch, filtersJson, arbeitFiltersJson, scope, includeTokens]);
  // „Verpasster Anruf" (User 01.10.2026) — eigener Baustein, table/verpassteAnrufe.tsx.
  // Wie Geburtstag/Neue Mail nur mit den Filtern des Users, ohne den Standard.
  const verpasst = useVerpassteAnrufe({ aktiv: arbeitsansicht, search: debouncedSearch, filtersJson: arbeitFiltersJson, scope, includeTokens });

  // ---- Magere Trefferliste macht auf „Alle" auf ----
  // Wer im Team-Bereich sucht, sucht einen Kontakt, nicht „im Team". Bleiben
  // die Treffer einstellig, liegt das meist daran, dass der Gesuchte einem
  // anderen Team oder niemandem gehört — auf PROD haben 2819 von 7786 Kontakten
  // gar kein BO-Team. Die Regel selbst steht in `kontaktsucheScope.ts`.
  //
  // `automatikGesperrtFuer` trägt den Suchbegriff, für den bereits entschieden
  // wurde — durch die Automatik ODER durch einen Klick des Nutzers. Ohne diese
  // Sperre spränge die Liste sofort zurück auf „Alle", sobald jemand während
  // derselben Suche wieder auf „Team" klickt: der Bereich ließe sich dann gar
  // nicht mehr wählen.
  const automatikGesperrtFuer = useRef<string | null>(null);
  const [autoErweitertFuer, setAutoErweitertFuer] = useState<string | null>(null);

  useEffect(() => {
    if (
      !sollAufAlleWechseln({
        scope,
        suchbegriff: debouncedSearch.trim(),
        laedt: loading,
        gesamt: total,
        hatMehr: hasMore,
        geladeneZeilen: rows.length,
        gesperrtFuer: automatikGesperrtFuer.current,
      })
    ) {
      return;
    }
    automatikGesperrtFuer.current = debouncedSearch.trim();
    setAutoErweitertFuer(debouncedSearch.trim());
    setScope("all");
  }, [scope, debouncedSearch, loading, total, hasMore, rows.length]);

  // Der Hinweis gehört zu GENAU dieser Suche — tippt jemand weiter, ist er weg.
  useEffect(() => {
    if (autoErweitertFuer !== null && autoErweitertFuer !== debouncedSearch.trim()) {
      setAutoErweitertFuer(null);
    }
  }, [debouncedSearch, autoErweitertFuer]);

  /** Klick auf den Bereichs-Umschalter: bewusste Wahl, die Automatik hält sich raus. */
  const scopeVonHandSetzen = useCallback(
    (naechster: ContactScope) => {
      automatikGesperrtFuer.current = debouncedSearch.trim();
      setAutoErweitertFuer(null);
      setScope(naechster);
    },
    [debouncedSearch],
  );

  // ---- Navigations-Zustand sichern + Scroll wiederherstellen ----
  const latestSnapRef = useRef<Omit<TableSnapshot, "scrollTop">>({
    search,
    filterValues,
    kStatusQuelle,
    boFoValue,
    sort,
    scope,
    loadedCount: rows.length,
  });
  latestSnapRef.current = { search, filterValues, kStatusQuelle, boFoValue, sort, scope, loadedCount: rows.length };
  const persistNav = useCallback(() => {
    saveNavState({ ...latestSnapRef.current, scrollTop: scrollRef.current?.scrollTop ?? 0 });
  }, [saveNavState]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(persistNav);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [persistNav]);
  useEffect(() => () => persistNav(), [persistNav]); // beim Verlassen (→ Detail) sichern
  // Scroll-Position einmalig wiederherstellen, sobald genug Zeilen geladen sind.
  const restoredScrollRef = useRef(false);
  useLayoutEffect(() => {
    if (restoredScrollRef.current) return;
    if (!restored) {
      restoredScrollRef.current = true;
      return;
    }
    if (rows.length >= Math.min(restored.loadedCount, 300)) {
      if (scrollRef.current) scrollRef.current.scrollTop = restored.scrollTop;
      restoredScrollRef.current = true;
    }
  }, [rows.length, restored]);

  // ---- Spalten-Präferenzen laden/speichern ----
  const prefKeyRef = useRef<string | null>(null);
  const hydratedRef = useRef(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const key = await getCurrentUserPreferenceKey();
      if (!alive) return;
      prefKeyRef.current = key;
      if (key) {
        try {
          const prefs = await fetchUserPreferences([
            konf.prefScopes.order,
            konf.prefScopes.visibility,
            konf.prefScopes.widths,
          ]);
          const ord = prefs[konf.prefScopes.order] as string[] | undefined;
          const vis = prefs[konf.prefScopes.visibility] as Record<string, boolean> | undefined;
          const widths = prefs[konf.prefScopes.widths] as Record<string, number> | undefined;
          if (!alive) return;
          if (istGueltigeReihenfolge(ord, konf.spaltenReihenfolge)) setColumnOrder(ord);
          if (vis && typeof vis === "object") {
            const clean: Record<string, boolean> = {};
            for (const id of konf.spaltenReihenfolge) if (vis[id] === false) clean[id] = false;
            setColumnVisibility(clean);
          }
          if (widths && typeof widths === "object") {
            const clean: Record<string, number> = {};
            for (const [id, w] of Object.entries(widths)) {
              if (typeof w === "number" && Number.isFinite(w) && w > 0) clean[id] = w;
            }
            setColumnWidths(clean);
          }
        } catch {
          /* Defaults beibehalten */
        }
      }
      hydratedRef.current = true;
    })();
    return () => {
      alive = false;
    };
  }, [konf]);
  // Ersten Lauf nach der Hydration NICHT speichern — nur den Ausgangsstand merken
  // (verhindert die 3 redundanten PUTs beim Laden, H2); danach nur bei echter Änderung.
  const savedSnapshotRef = useRef<string | null>(null);
  useEffect(() => {
    if (!hydratedRef.current || !prefKeyRef.current) return;
    const snapshot = JSON.stringify([columnOrder, columnVisibility, columnWidths]);
    if (savedSnapshotRef.current === null) {
      savedSnapshotRef.current = snapshot;
      return;
    }
    if (snapshot === savedSnapshotRef.current) return;
    savedSnapshotRef.current = snapshot;
    const key = prefKeyRef.current;
    const t = setTimeout(() => {
      saveUserPreference(key, konf.prefScopes.order, columnOrder).catch(() => {});
      saveUserPreference(key, konf.prefScopes.visibility, columnVisibility).catch(() => {});
      saveUserPreference(key, konf.prefScopes.widths, columnWidths).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [columnOrder, columnVisibility, columnWidths, konf]);

  // ---- BO-Teams + FO-User (gfgl) für den Verantwortlich-Filter ----
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const teams = await fetchTeamsLite();
        if (alive) {
          setTeamOptions(
            teams
              .map((t: any) => ({ id: String(t.id), label: String(t.name ?? "").replace(/^Team\s+/i, "") }))
              .filter((o) => o.id && o.label),
          );
        }
      } catch (e) {
        console.error("teams-lite laden fehlgeschlagen", e);
      }
      try {
        const users = await listAppUsers();
        if (alive) {
          setUserOptions(
            users
              // FO = Front-Office: department enthält "FO" (z. B. "FO" oder "FO+BO"),
              // nicht role-basiert (Mitarbeiter sind ebenfalls FO). Admins ausgenommen.
              .filter((u) => (u.department ?? "").split("+").includes("FO") && (u.role ?? "").toLowerCase() !== "admin")
              .map((u) => ({ id: String(u.id), label: [u.lastName, u.firstName].filter(Boolean).join(", ") || u.name || u.email || u.id }))
              .filter((o) => o.id && o.label),
          );
        }
      } catch (e) {
        console.error("users laden fehlgeschlagen", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ---- Auswahl ----
  const allOn = angezeigteZeilen.length > 0 && angezeigteZeilen.every((r) => selectedIds.has(r.kontaktId));
  const toggle = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelectedIds(() => (allOn ? new Set<number>() : new Set(angezeigteZeilen.map((r) => r.kontaktId))));

  const updateFilter = useCallback((columnId: string, value: string | boolean | undefined) => {
    // Den K-Status-Filter angefasst (angewendet ODER geleert) → er gilt ab
    // jetzt auch bei der Suche.
    if (columnId === K_STATUS_SPALTE) setKStatusQuelle("user");
    setFilterValues((prev) => {
      const next = { ...prev };
      if (value === undefined || value === "" || value === false) delete next[columnId];
      else next[columnId] = value;
      return next;
    });
  }, []);

  // Mobiles Filter-Sheet: K-Verantwortlicher (boFo) toggeln + alles zurücksetzen.
  const toggleBoTeam = (idv: string) =>
    setBoFoValue((v) => ({
      ...v,
      boTeamIds: v.boTeamIds.includes(idv) ? v.boTeamIds.filter((x) => x !== idv) : [...v.boTeamIds, idv],
    }));
  const toggleFoUser = (idv: string) =>
    setBoFoValue((v) => ({
      ...v,
      foUserIds: v.foUserIds.includes(idv) ? v.foUserIds.filter((x) => x !== idv) : [...v.foUserIds, idv],
    }));
  /** „Zurücksetzen": zurück zum Standard der Variante (Kontakte: Kunde + Interessent). */
  const clearAllFilters = () => {
    setFilterValues(standardFilterWerte(variante));
    setKStatusQuelle(anfangsFilter(variante, null).kStatusQuelle);
    setBoFoValue({ boTeamIds: [], foUserIds: [] });
  };
  /** „Ohne Filter … anzeigen": wirklich ALLE Filter weg, auch der Standard. */
  const ohneJedenFilter = () => {
    setFilterValues({});
    setKStatusQuelle("user");
    setBoFoValue({ boTeamIds: [], foUserIds: [] });
  };

  const openContact = (r: Row) => {
    // Geordnete ID-Liste der aktuell geladenen Zeilen + Position mitgeben, damit
    // die Detailseite Prev/Next ohne 404 (gelöschte IDs) navigieren kann.
    const orderedIds = angezeigteZeilen.map((row) => row.kontaktId);
    navigate(`/kontakte/edit/${r.kontaktId}`, {
      state: {
        k: { name: r.displayName ?? "", typ: String(r.typ ?? ""), status: r.kontaktStatus },
        navContext: { orderedIds, currentIndex: orderedIds.indexOf(r.kontaktId) },
      },
    });
  };

  // ---- Auswahl-Aktionen (wie früher) ----
  const handleEditSelected = () => {
    const id = Array.from(selectedIds)[0];
    if (id == null) return;
    const r = rows.find((x) => x.kontaktId === id);
    if (r) openContact(r);
    else navigate(`/kontakte/edit/${id}`);
  };
  const handleMerge = () => {
    const ids = Array.from(selectedIds);
    if (ids.length < 2) return;
    navigate(`/contacts/merge?clusterIds=${ids.join(",")}`);
  };

  // ---- Bulk-Löschen (admin/gfgl) — DELETE /contact/bulk?ids=… ----
  const handleDeleteSelected = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (ids.length > 200) {
      void hinweisen({
        titel: "Zu viele Kontakte ausgewählt",
        text: `Es lassen sich höchstens 200 Kontakte auf einmal löschen – ausgewählt sind ${ids.length}. Bitte die Auswahl verkleinern.`,
      });
      return;
    }
    try {
      const { data } = await apiFetch<{ deleted: number[]; skipped: Array<{ id: number; reason: "not_found" | "forbidden" }> }>(
        "contact/bulk",
        { method: "DELETE", params: { ids: ids.join(",") } },
      );
      removeByKeys(data.deleted);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of data.deleted) next.delete(id);
        return next;
      });
      if (data.skipped && data.skipped.length > 0) {
        const c = data.skipped.reduce((acc, s) => ({ ...acc, [s.reason]: (acc[s.reason] ?? 0) + 1 }), {} as Record<string, number>);
        const parts = [
          `${data.deleted.length} gelöscht`,
          c.forbidden ? `${c.forbidden} ohne Berechtigung` : null,
          c.not_found ? `${c.not_found} nicht gefunden` : null,
        ].filter(Boolean);
        void hinweisen({ titel: "Nicht alle Kontakte gelöscht", text: parts.join(", ") + "." });
      }
    } catch (err) {
      console.error("Bulk delete failed", err);
      void hinweisen({ titel: "Löschen fehlgeschlagen", text: "Die Kontakte konnten nicht gelöscht werden. Bitte später noch einmal versuchen.", ton: "danger" });
    }
  }, [selectedIds, removeByKeys]);

  const toggleCollapsed = useCallback(
    (id: string) => setCollapsedColumns((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
    [],
  );

  // ---- Spalten ----
  const columns: Column<Row>[] = useMemo(() => {
    const alle: Column<Row>[] = [
      {
        id: "select",
        className: "colcheck",
        pinned: true,
        frozen: true,
        header: <Checkbox checked={allOn} onChange={toggleAll} ariaLabel="Alle auswählen" />,
        cell: (r) => <Checkbox checked={selectedIds.has(r.kontaktId)} onChange={() => toggle(r.kontaktId)} ariaLabel="Auswählen" />,
      },
      {
        id: "Typ",
        header: "Typ",
        pinned: true,
        frozen: true,
        sortable: true,
        sortField: "Typ",
        // Werte bleiben die gespeicherten (Backend-Filter „Typ"), nur die
        // Beschriftung heißt „Privat".
        filter: { kind: "dropdown", options: ["Firma", "Person"], optionLabel: contactTypeLabel },
        cell: (r) => <TypeCell icon={r.typ === "Firma" ? FirmaIcon : PersonIcon} label={contactTypeLabel(String(r.typ ?? "—"))} />,
      },
      // Dieselbe Stelle in „CO intern": Dort ist praktisch jede Zeile eine
      // Firma, und „Firma" 140-mal untereinander beantwortet keine Frage. Die
      // Frage hier lautet „Lieferant oder Mitgliedschaft?". Das Symbol bleibt
      // Firma/Person — es sagt weiterhin, WAS der Kontakt ist, während die
      // Beschriftung sagt, was er FÜR UNS ist.
      //
      // Eigene Id (`CoArt`) statt „Typ": An der Id hängt der Filterschlüssel
      // ans Backend, und `CoArt` trifft dort sowohl `coArten` als auch den
      // Kontaktstatus.
      {
        id: CO_ART_SPALTE,
        header: "Typ",
        pinned: true,
        frozen: true,
        // Bewusst NICHT sortierbar: Der Server sortiert „Typ" nach
        // Firma/Person. Nach der CO-Art kann er nicht sortieren, und eine
        // Sortierung, die etwas anderes tut als die Spalte zeigt, ist
        // schlimmer als keine. Zum Gruppieren gibt es den Filter daneben.
        filter: { kind: "dropdown", options: CO_ARTEN },
        minWidth: 168,
        resizable: true,
        cell: (r) => {
          const arten = coArtenDerZeile(r.coArten, r.kontaktStatus);
          if (!arten.length) return <Dash />;
          return (
            <TypeCell
              icon={r.typ === "Firma" ? FirmaIcon : PersonIcon}
              label={arten.join(", ")}
            />
          );
        },
      },
      {
        id: "Name",
        header: "Name",
        className: "name kt-wrap",
        pinned: true,
        frozen: true,
        sortable: true,
        sortField: "Name",
        filter: { kind: "text" },
        minWidth: 240,
        resizable: true,
        // Das Logo steht VOR dem Namen und hat keine eigene Spalte: Es ist die
        // Marke derselben Organisation, deren Name daneben steht — und es
        // findet die Zeile schneller wieder, als das Auge den Namen liest.
        // `coIntern` liefert der Server nur an die Liste „CO intern"; in der
        // Kontaktliste bleibt die Zelle unverändert.
        cell: (r) => {
          // Das Kreuz gehört zur ANZEIGE, nicht zum Namen: `displayName` wird
          // anderswo als Wert weitergereicht (Suche, Betreff, Export). Deshalb
          // steht es hier als eigenes Element davor und nicht im String —
          // dieselbe Trennung wie im Kopf der Akte (utils/contactDisplay.ts).
          const tot = istVerstorben(r as any);
          const namensText = r.umfeldRolle?.ausgeschieden ? (
            <span className="kt-umfeld-aus-name">{r.displayName || "—"}</span>
          ) : (
            r.displayName || <Dash />
          );
          const mitKreuz = tot ? (
            <>
              <span
                aria-label="verstorben"
                title={verstorbenTitel(r as any)}
                style={{ opacity: 0.7, marginRight: 4 }}
              >
                {VERSTORBEN_ZEICHEN}
              </span>
              {namensText}
            </>
          ) : (
            namensText
          );
          const zusatz = r.arbeitsZusatz ? (
            <ArbeitsZusatzZeile zusatz={r.arbeitsZusatz} />
          ) : r.umfeldRolle?.teile.length ? (
            <UmfeldRolleZeile rolle={r.umfeldRolle} />
          ) : null;
          const name = r.coIntern?.logoDatei ? (
            <span className="co-namenszeile">
              <CoLogo datei={r.coIntern.logoDatei} />
              <span className="co-namenstext">{mitKreuz}</span>
            </span>
          ) : (
            mitKreuz
          );
          return zusatz ? (
            <>
              <span style={{ display: "block" }}>{name}</span>
              {zusatz}
            </>
          ) : (
            name
          );
        },
      },
      {
        // Zusammengeführt aus Ort + Straße. id bleibt "Ort" → Sort/Text-Filter
        // (Backend-Key "Ort") unverändert; PLZ läuft im Hintergrund mit (Maps).
        id: "Ort",
        header: "Anschrift",
        className: "kt-wrap",
        sortable: true,
        sortField: "Ort",
        filter: { kind: "text" },
        hideable: true,
        collapsible: true,
        minWidth: 200,
        resizable: true,
        cell: (r) => {
          const ort = (r.ort ?? "").trim();
          const strasse = (r.strasse ?? "").trim();
          const plz = (r.plz ?? "").trim();
          const land = ((r as any).land ?? "").trim();
          // Das Inland bleibt stumm: Stünde überall „Deutschland", wüchse jede
          // Zeile um eine Angabe, die niemand braucht. Sichtbar wird das Land
          // genau dort, wo es die Adresse erst lesbar macht.
          const ausland = auslandsHinweis(land);
          const text = [[ort, strasse].filter(Boolean).join(", "), ausland]
            .filter(Boolean)
            .join(" · ");
          if (!text && !plz) return <Dash />;
          return (
            <span className="anschrift-cell">
              <span className="anschrift-text">{text || <Dash />}</span>
              <a
                className="maps-link"
                href={mapsUrl({ strasse, plz, ort, land })}
                target="_blank"
                rel="noopener noreferrer"
                title="In Google Maps öffnen"
                aria-label="In Google Maps öffnen"
                onClick={(e) => e.stopPropagation()}
              >
                {MapPinIcon}
              </a>
            </span>
          );
        },
      },
      {
        id: "kmoeglichkeit",
        header: "K-Möglichkeit",
        hideable: true,
        collapsible: true,
        filter: { kind: "text" },
        cell: (r) => {
          const hasComm =
            (r.phones?.length ?? 0) > 0 ||
            (r.emails?.length ?? 0) > 0 ||
            (r.socialMedia?.length ?? 0) > 0;
          return hasComm ? <KontaktmoeglichkeitCell row={r} /> : <Dash />;
        },
      },
      {
        id: "Kontaktstatus",
        header: "K-Status",
        sortable: true,
        sortField: "Kontaktstatus",
        // Kunde | Interessent | Sonstige → Bereich/Rolle wie in der Anlage
        // (User 28.09.2026).
        filter: {
          kind: "custom",
          width: 320,
          render: (a) => <KStatusFilter value={a.value} onChange={a.onChange} close={a.close} />,
        },
        hideable: true,
        collapsible: true,
        cell: (r) => {
          if (!r.kontaktStatus) return <Dash />;
          if (STATUS_MIT_EIGENEM_CHIP.includes(r.kontaktStatus)) {
            // Fallback-Tönung, falls die Liste einmal wächst, ohne dass
            // STATUS_TONE mitzieht: ein Chip ganz ohne Klasse wäre unsichtbar.
            return (
              <Chip tone={STATUS_TONE[r.kontaktStatus] ?? "chip-sonstige"}>
                {r.kontaktStatus}
              </Chip>
            );
          }
          return <StatusPopover status={r.kontaktStatus} />;
        },
      },
      {
        id: "verantwortlich",
        header: (
          <span className="kt-vr-head">
            <span className="kt-vr-title">K-Verantwortlicher</span>
            <span className="kt-vr-sub">BO | FO</span>
          </span>
        ),
        filter: { kind: "boFo", boOptions: teamOptions, foOptions: userOptions },
        hideable: true,
        collapsible: true,
        cell: (r) => {
          // Struktur „BO | FO": BO = Team-Chip (voller Name, eigene Farbe);
          // FO = User-Kreis (Kürzel/Initialen, eigene Farbe). Fehlende Seite → „–".
          // Geteilte Darstellung via FoBoBadge (SP6); leere Zelle bleibt der
          // gemutete „—"-Dash der Tabelle.
          // Mehrere FO (28.09.2026): je FO ein Kürzel-Kreis nebeneinander.
          const { boLabel, foLabel } = foBoLabels(r.boTeam, r.foUser);
          if (!boLabel && !foLabel && !(r.foUsers?.length)) return <Dash />;
          return <FoBoBadge boTeam={r.boTeam} foUser={r.foUser} foUsers={r.foUsers} />;
        },
      },
      {
        id: VORGANG_SPALTE,
        header: "Vorgänge",
        // T-284: „Alle offenen Vorgänge" oder nur offene einer Art (NKG, JG, …).
        filter: VORGANG_SPALTEN_FILTER,
        hideable: true,
        collapsible: true,
        cell: (r) => {
          const hasV = r.openEventTypes?.includes("V");
          const nkgId = r.nkgVorgangId;
          // T/S/Z liefert der Server nur in der Arbeitsansicht (`?include=arbeit`).
          const hasT = r.openEventTypes?.includes("T");
          const hasS = r.openEventTypes?.includes("S");
          const hasZ = r.openEventTypes?.includes("Z");
          if (!hasV && !nkgId && !hasT && !hasS && !hasZ) return <Dash />;
          return (
            <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
              {hasV ? (
                <button
                  type="button"
                  className="kt-vorgang-btn"
                  title="Offene Vorgänge anzeigen"
                  onClick={(e) => {
                    e.stopPropagation();
                    setVorgaengeFor({ id: r.kontaktId, name: r.displayName ?? "" });
                  }}
                >
                  <VorgangBadge title="Offene Vorgänge" />
                </button>
              ) : null}
              {nkgId ? (
                <button
                  type="button"
                  className="kt-vorgang-btn"
                  title="NKG-Vorgang öffnen"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/vorgaenge/${nkgId}`);
                  }}
                >
                  <span className="nkg-pill" title="NKG-Vorgang">NKG</span>
                </button>
              ) : null}
              <OffenePunkteBadges openEventTypes={r.openEventTypes} />
            </span>
          );
        },
      },
      {
        id: "Kundenverbindungen",
        header: "Kundenverbindungen",
        hideable: true,
        collapsible: true,
        cell: (r) =>
          r.group?.type === "Kundenverbindung" ? (
            <KundenverbindungCell group={{ id: r.group.id, name: r.group.name }} count={kvCounts[r.group.id]} />
          ) : (
            <Dash />
          ),
      },
      {
        id: "Relevanz",
        header: "K-Relevanz",
        hideable: true,
        collapsible: true,
        cell: (r) => {
          // Interessent mit Phase → Phase-Text; sonst immer Gold-Sterne (0–5, 0 wenn keine Daten).
          if (r.kontaktStatus === "Interessent" && r.phase) return <span>{r.phase}</span>;
          return <StarRating value={Number(r.relevanz ?? 0)} />;
        },
      },
      {
        id: "Wert",
        header: "K-Wert",
        align: "right",
        hideable: true,
        collapsible: true,
        cell: (r) => (r.value ? `${r.value} €` : <Dash />),
      },
      // ---- Die Felder der Lenkungsliste -------------------------------
      // Reihenfolge und Beschriftungen wie in der Mappe: Wer sie kennt, sucht
      // die Angaben an der gewohnten Stelle. Alle zusammen erzeugt und unten
      // in einem Zug herausgefiltert, wenn die Variante sie nicht zeigt.
      //
      // `r.coIntern` kommt nur, wenn die Liste `?include=cointern` geschickt
      // hat UND der Server das Recht bejaht — sonst ist es null, und jede
      // dieser Zellen bleibt leer.
      {
        id: "CoKategorie",
        header: "Kategorie",
        // Der naheliegende Einstieg in diese Liste: „zeig mir die Handwerker".
        // Der Filter läuft serverseitig (`CoKategorie`), damit er auch über
        // die noch nicht geladenen Seiten wirkt.
        filter: { kind: "dropdown", options: CO_KATEGORIEN },
        hideable: true,
        collapsible: true,
        minWidth: 150,
        resizable: true,
        cell: (r) => r.coIntern?.kategorie || <Dash />,
      },
      {
        id: "CoLeistung",
        header: "Leistung",
        className: "kt-wrap",
        hideable: true,
        collapsible: true,
        minWidth: 200,
        resizable: true,
        cell: (r) =>
          r.coIntern?.leistung ? <CoMehrzeilig wert={r.coIntern.leistung} /> : <Dash />,
      },
      {
        id: "CoSharePoint",
        header: "SharePoint",
        hideable: true,
        collapsible: true,
        minWidth: 160,
        resizable: true,
        cell: (r) =>
          r.coIntern?.sharepointUrl ? (
            <CoLink href={r.coIntern.sharepointUrl} text={r.displayName} titel="QM-Ordner" />
          ) : (
            <Dash />
          ),
      },
      {
        id: "CoWebseite",
        header: "Webseite",
        hideable: true,
        collapsible: true,
        minWidth: 160,
        resizable: true,
        cell: (r) =>
          r.coIntern?.webseite ? (
            <CoLink href={r.coIntern.webseite} text={r.displayName} titel="Website" />
          ) : (
            <Dash />
          ),
      },
      {
        id: "CoPortal",
        header: "Portalzugang",
        hideable: true,
        collapsible: true,
        minWidth: 160,
        resizable: true,
        cell: (r) => {
          const co = r.coIntern;
          if (!co?.portalUrl && !co?.portalText) return <Dash />;
          // Die Mappe führt hier mal eine Adresse, mal nur einen Namen („KARAU
          // Prüfservice GmbH / Ihr Prüfdienst"). Ohne Adresse bleibt der Text
          // stehen — er ist der Hinweis, WO der Zugang liegt.
          return <CoLink href={co.portalUrl} text={co.portalText || r.displayName} titel="Portal" />;
        },
      },
      {
        id: "CoVertragsnummer",
        header: "Vertrags-/Kundennr.",
        className: "kt-wrap",
        hideable: true,
        collapsible: true,
        minWidth: 160,
        resizable: true,
        cell: (r) =>
          r.coIntern?.vertragsnummer ? (
            <CoMehrzeilig wert={r.coIntern.vertragsnummer} />
          ) : (
            <Dash />
          ),
      },
      {
        id: "CoQmRelevant",
        header: "QM-relevant",
        hideable: true,
        collapsible: true,
        minWidth: 120,
        cell: (r) => r.coIntern?.qmRelevant || <Dash />,
      },
      {
        id: "CoVertragsart",
        header: "Vertragsart",
        hideable: true,
        collapsible: true,
        minWidth: 150,
        resizable: true,
        cell: (r) => r.coIntern?.vertragsart || <Dash />,
      },
      {
        id: "CoKosten",
        header: "Kosten",
        className: "kt-wrap",
        hideable: true,
        collapsible: true,
        minWidth: 150,
        resizable: true,
        // Freitext, nicht rechtsbündig: „375,00 EUR HJ." und „39,00 EUR je
        // Fahrzeug" sind keine Beträge, die man untereinander vergleicht.
        cell: (r) => (r.coIntern?.kosten ? <CoMehrzeilig wert={r.coIntern.kosten} /> : <Dash />),
      },
      {
        id: "CoBeginn",
        header: "Beginn",
        hideable: true,
        collapsible: true,
        minWidth: 110,
        cell: (r) => r.coIntern?.beginn || <Dash />,
      },
      {
        id: "CoAblauf",
        header: "Ablauf",
        hideable: true,
        collapsible: true,
        minWidth: 110,
        cell: (r) => r.coIntern?.ablauf || <Dash />,
      },
      {
        id: "CoTurnus",
        header: "Turnus",
        hideable: true,
        collapsible: true,
        minWidth: 110,
        cell: (r) => r.coIntern?.turnus || <Dash />,
      },
      {
        id: "CoAutoVerlaengerung",
        header: "Autom. Verlängerung",
        hideable: true,
        collapsible: true,
        minWidth: 130,
        cell: (r) => r.coIntern?.autoVerlaengerung || <Dash />,
      },
      {
        id: "CoKuendigungsfrist",
        header: "Kündigungsfrist",
        className: "kt-wrap",
        hideable: true,
        collapsible: true,
        minWidth: 160,
        resizable: true,
        cell: (r) =>
          r.coIntern?.kuendigungsfrist ? (
            <CoMehrzeilig wert={r.coIntern.kuendigungsfrist} />
          ) : (
            <Dash />
          ),
      },
      {
        id: "CoErstbewertung",
        header: "Erstbewertungskriterien",
        className: "kt-wrap",
        hideable: true,
        collapsible: true,
        minWidth: 190,
        resizable: true,
        cell: (r) =>
          r.coIntern?.erstbewertung ? (
            <CoMehrzeilig wert={r.coIntern.erstbewertung} />
          ) : (
            <Dash />
          ),
      },
      {
        id: "CoBewertung",
        header: "Bewertung",
        // `kt-wrap` deckelt die Zelle (`max-width: 0`), damit ein Freitext in
        // der Notenspalte gekürzt wird, statt die Spalte aufzuziehen.
        className: "kt-wrap",
        hideable: true,
        collapsible: true,
        minWidth: 100,
        resizable: true,
        cell: (r) =>
          r.coIntern?.bewertung ? (
            <CoNote wert={r.coIntern.bewertung} titel="Jüngste Bewertung" />
          ) : (
            <Dash />
          ),
      },
      // Eine Spalte je Halbjahr, wie in der Mappe: Spalte T bis AF, jüngstes
      // Halbjahr links. Die Reihe steht in `CO_HALBJAHRE` und nicht in den
      // geladenen Daten — ein Halbjahr ohne Bewertung ist im QM-Nachweis eine
      // Aussage und darf nicht einfach fehlen.
      ...CO_HALBJAHRE.map((h): Column<Row> => ({
        id: h.spalte,
        // Zweizeilig, damit die Spalte schmal bleiben kann: Ausgeschrieben
        // („Bewertung Schulnotensystem 2025 - 2. Halbj.", so steht es in der
        // Mappe) wäre jede der dreizehn Spalten dreimal so breit wie ihr Inhalt.
        header: (
          <span className="co-hj-kopf">
            <span className="co-hj-jahr">{h.jahr}</span>
            <span className="co-hj-teil">{h.teil}</span>
          </span>
        ),
        hideable: true,
        collapsible: true,
        align: "center",
        minWidth: 78,
        // Kein Strich, wenn nichts bewertet wurde: In EINER Spalte markiert er
        // die Lücke, in dreizehn nebeneinander ist er nur noch Rauschen — und
        // in der Mappe ist das Feld dort schlicht leer.
        cell: (r) => <CoNote wert={r.coIntern?.bewertungen?.[h.periode]} titel={halbjahrLabel(h.periode)} />,
      })),
      {
        id: "CoAktiv",
        header: "Status",
        filter: { kind: "boolean", label: "Nur aktive" },
        hideable: true,
        collapsible: true,
        minWidth: 110,
        cell: (r) =>
          r.coIntern?.aktiv === true || r.coIntern?.aktiv === false ? (
            <CoAktiv aktiv={r.coIntern.aktiv} />
          ) : (
            <Dash />
          ),
      },
    ];
    // Die Zusatzspalten fallen in einem Zug heraus, wenn die Variante sie
    // nicht zeigt — so bleibt die Reihenfolge oben an EINER Stelle lesbar,
    // statt das Array zu verzweigen.
    const weg = new Set<string>();
    // Die beiden Typ-Spalten schliessen sich aus — sie stehen an derselben
    // Stelle und beantworten dieselbe Frage für ihre jeweilige Liste.
    weg.add(konf.typSpalteZeigtCoArt ? "Typ" : CO_ART_SPALTE);
    if (!konf.zeigtCoSpalten) for (const id of CO_SPALTEN) weg.add(id);
    return alle.filter((c) => !weg.has(String(c.id)));
  }, [allOn, angezeigteZeilen, selectedIds, teamOptions, userOptions, kvCounts, konf]);

  // ---- Abschnitt „Unternehmensverbund von …" / „Haushalt von …" ----
  const umfeldAbschnitt: DataTableAbschnitt<Row> | null = useMemo(() => {
    const plan = umfeldDaten.plan;
    if (!plan || !umfeld.gruppen.length) return null;
    const anzahl = umfeld.gruppen.reduce((n, g) => n + g.zeilen.length, 0);
    return {
      key: "umfeld",
      className: "sec-umfeld",
      kopf: (
        <span className="kt-sec-btn kt-sec-statisch" role="heading" aria-level={3}>
          <span className="kt-sec-ico">{plan.art === "firma" ? FirmaIcon : PersonIcon}</span>
          <span>
            {plan.kopf} <strong>{plan.name}</strong>
          </span>
          <span className="kt-sec-n">{anzahl}</span>
        </span>
      ),
      teile: umfeld.gruppen.map((g) => ({
        key: g.key,
        // Bei nur einer Gruppe (Haushalt) sagt der Kopf schon alles.
        titel: umfeld.gruppen.length > 1 || plan.art === "firma" ? `${g.titel} · ${g.zeilen.length}` : undefined,
        zeilen: g.zeilen,
      })),
    };
  }, [umfeldDaten.plan, umfeld]);

  // ---- Abschnitte der Arbeitsansicht ----
  const abschnitte: DataTableAbschnitt<Row>[] | undefined = useMemo(() => {
    if (!arbeitsansicht) {
      // Ohne Arbeitsansicht nur, wenn es ein Umfeld gibt: die Treffer ohne
      // Kopfzeile (die Tabelle sieht aus wie immer), das Umfeld dahinter.
      return umfeldAbschnitt
        ? [{ key: "treffer", kopf: null, teile: [{ key: "alle", zeilen: trefferRows }] }, umfeldAbschnitt]
        : undefined;
    }
    const heute = new Date();
    const chev = (
      <span className="kt-sec-chev" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M5 8l5 5 5-5" /></svg>
      </span>
    );
    const kopf = (k: AbschnittKey, icon: React.ReactNode, titel: string, n: number | null, sub?: string) => (
      <button type="button" className="kt-sec-btn" aria-expanded={abschnitteOffen[k]} onClick={() => abschnittUmschalten(k)}>
        {chev}
        <span className="kt-sec-ico">{icon}</span>
        {titel}
        {n !== null && <span className="kt-sec-n">{n}</span>}
        {sub && <span className="kt-sec-sub">{sub}</span>}
      </button>
    );
    const liste: DataTableAbschnitt<Row>[] = [];
    if (geburtstage.length) {
      liste.push({
        key: "gb",
        className: "sec-gb",
        kopf: kopf("gb", ICON_TORTE, "Hat heute Geburtstag", geburtstage.length),
        teile: [
          {
            key: "gb",
            zeilen: abschnitteOffen.gb
              ? geburtstage.map((r) => {
                  const alter = alterAm(r.birthDate, heute);
                  return { ...r, arbeitsZusatz: { art: "gb" as const, text: alter !== null ? `wird heute ${alter}` : "hat heute Geburtstag" } };
                })
              : [],
          },
        ],
      });
    }
    if (neueMails.length) {
      liste.push({
        key: "mail",
        className: "sec-mail",
        kopf: kopf("mail", ICON_MAIL, "Neue Mail", neueMails.length, "neueste zuerst"),
        teile: [
          {
            key: "mail",
            zeilen: abschnitteOffen.mail
              ? neueMails.map((r) => ({
                  ...r,
                  arbeitsZusatz: {
                    art: "mail" as const,
                    zeit: r.neueMail ? mailZeitLabel(r.neueMail.zeit, heute) : "",
                    betreff: r.neueMail?.betreff ?? "",
                  },
                }))
              : [],
          },
        ],
      });
    }
    const anrufAbschnitt = verpasstAbschnitt<Row>({
      anrufe: verpasst,
      offen: abschnitteOffen.anruf,
      kopf: kopf("anruf", ICON_TELEFON, "Verpasster Anruf", verpasst.zeilen.length, "diese Woche, persönlich und Team"),
      jetzt: heute,
    });
    if (anrufAbschnitt) liste.push(anrufAbschnitt);
    const offen = trefferRows.filter(hatOffenePunkte);
    const rest = trefferRows.filter((r) => !hatOffenePunkte(r));
    liste.push({
      key: "all",
      className: "sec-all",
      kopf: kopf(
        "all",
        ICON_MENSCHEN,
        "Alle Kontakte",
        total ?? rows.length,
        arbeitsSortierung ? "offene Vorgänge, Termine, Schäden, Zahlungsverkehr zuerst, dann A–Z" : undefined,
      ),
      teile: !abschnitteOffen.all
        ? []
        : arbeitsSortierung
          ? [
              ...(offen.length
                ? [{ key: "offen", titel: `Mit offenen Punkten · ${offenAnzahl ?? offen.length}`, zeilen: offen }]
                : []),
              ...(rest.length
                ? [
                    {
                      key: "rest",
                      titel: `Übrige Kontakte A–Z${offenAnzahl !== null && total != null ? ` · ${Math.max(total - offenAnzahl, 0)}` : ""}`,
                      zeilen: rest,
                    },
                  ]
                : []),
              ...(!rows.length && !loading ? [{ key: "leer", titel: "Kein Kontakt passt zur Suche.", zeilen: [] }] : []),
            ]
          : [{ key: "alle", zeilen: trefferRows }],
    });
    if (umfeldAbschnitt) liste.push(umfeldAbschnitt);
    return liste;
    // abschnittUmschalten ist stabil genug (setState-Updater); bewusst nicht in den deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arbeitsansicht, abschnitteOffen, geburtstage, neueMails, verpasst, rows, trefferRows, umfeldAbschnitt, total, offenAnzahl, arbeitsSortierung, loading]);

  // Was die Liste gerade wirklich einschränkt (bei Suche ohne den Standard).
  const activeFilterCount = zaehlbareKontaktFilter(effektiveWerte, boFoValue);
  const weichtAb = weichtVomStandardAb({ variante, filterValues, kStatusQuelle, boFo: boFoValue });

  // ---- „Ohne Filter gäbe es N Treffer" (wie bei Vorgänge) ----
  // Gezählt mit derselben Suche und demselben Bereich, nur ohne Spaltenfilter
  // (der feste Filter der Variante bleibt — „CO intern" bleibt „CO intern").
  const ohneFilterJson = JSON.stringify(konf.festerFilter ?? {});
  const ohneFilter = useOhneFilterProbe({
    treffer: rows.length,
    laedt: loading,
    filterAnzahl: activeFilterCount,
    zaehlen: (signal) =>
      apiFetch<any[]>("contact", {
        signal,
        params: {
          limit: 1,
          offset: 0,
          search: debouncedSearch,
          filters: ohneFilterJson,
          scope,
          include: konf.includeTokens,
          sort: "",
          direction: "",
        },
      }).then(({ total: t }) => t),
    schluessel: `${debouncedSearch}|${scope}|${ohneFilterJson}`,
  });
  const zeigeOhneFilter = () => {
    ohneJedenFilter();
    ohneFilter.verwerfen();
  };

  // ---- Sichtbare Filterzeile über der Tabelle (User 01.10.2026) ----
  // „User vergessen oft, dass sie Filter gesetzt haben." Die Zeile nennt jeden
  // selbst gesetzten Filter; der Standard bleibt ungenannt.
  const filterTeile: { key: string; text: string }[] = [];
  for (const [k, v] of Object.entries(filterValues)) {
    if (v === undefined || v === "" || v === false) continue;
    if (k === K_STATUS_SPALTE) {
      if (standardAktiv(filterValues, kStatusQuelle)) continue; // Standard bleibt ungenannt
      filterTeile.push({ key: k, text: wahlBeschreibung(kStatusWahlAusFilterWert(String(v))) || "K-Status" });
      continue;
    }
    const spalte = columns.find((c) => c.id === k);
    const titel = typeof spalte?.header === "string" ? spalte.header : k;
    filterTeile.push({ key: k, text: v === true ? titel : `${titel}: ${String(v)}` });
  }
  if (boFoValue.boTeamIds.length + boFoValue.foUserIds.length > 0) {
    const namen = [
      ...teamOptions.filter((o) => boFoValue.boTeamIds.includes(o.id)).map((o) => o.label),
      ...userOptions.filter((o) => boFoValue.foUserIds.includes(o.id)).map((o) => o.label),
    ];
    filterTeile.push({ key: "boFo", text: `K-Verantwortlicher: ${namen.join(", ") || "gewählt"}` });
  }
  // Der Standard Kunde + Interessent steht NICHT in der Zeile — „das wissen
  // die Mitarbeiter schon" (User 02.10.2026). Gezeigt wird nur, was der User
  // selbst gefiltert hat.
  const filterZeile =
    filterTeile.length === 0 && !weichtAb ? null : (
      <div className="kt-filterzeile" role="status" aria-live="polite">
        {filterTeile.length > 0 ? (
          <span className="kt-filterzeile-teil">
            <span className="kt-filterzeile-titel">Gefiltert:</span> {filterTeile.map((t) => t.text).join(" · ")}
          </span>
        ) : null}
        {weichtAb ? (
          <button type="button" className="kt-filterzeile-btn" onClick={clearAllFilters}>
            Zurücksetzen
          </button>
        ) : null}
      </div>
    );

  // „Arbeitsansicht" (User 28.09.2026) — ersetzt den Vorfilter „Sonstige
  // Kontakte"; der Filter nach Sonstigen sitzt jetzt im K-Status-Spaltenfilter.
  // Die Kundenverbindungen sind eine Karte im Dashboard.
  const arbeitsansichtBtn = !konf.zeigtSonstigeVorfilter ? null : (
    <button
      type="button"
      className={"kv-toggle" + (arbeitsansicht ? " on" : "")}
      aria-pressed={arbeitsansicht}
      title="Geburtstage, neue Mails und offene Punkte zuerst"
      onClick={() =>
        setArbeitsansicht((an) => {
          arbeitsansichtSpeichern(!an);
          return !an;
        })
      }
    >
      Arbeitsansicht
    </button>
  );

  // Sub-Zeile zwischen Kopfleiste und Tabelle — scrollt mit weg (nicht sticky),
  // nur der Tabellen-Header bleibt fix. Links die Arbeitsansicht, rechts
  // Raster und Spalten.
  const tableSubBar = (
    <>
    {filterZeile}
    <div className="kt-subbar">
      {arbeitsansichtBtn}
      <div className="kt-subbar-spacer" />
      <button
        type="button"
        className={"kv-toggle" + (gridOn ? " on" : "")}
        aria-pressed={gridOn}
        onClick={() => setGridOn((g) => !g)}
      >
        Raster anzeigen
      </button>
      <ColumnsMenu
        columns={columns}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={toggleCollapsed}
      />
    </div>
    </>
  );

  // Bottom-Leiste (Auswahl-Aktionen) — in Tabellen- UND Karten-Ansicht gleich.
  const bottomBar = (
    <TableBottomBar
      selectionCount={selectedIds.size}
      filteredTotal={total}
      filterActive={activeFilterCount > 0}
      actions={
        <>
          {selectedIds.size >= 2 ? (
            <button className="tbar-btn" onClick={handleMerge}>{I.merge}Zusammenführen</button>
          ) : null}
          {selectedIds.size === 1 ? (
            <button className="tbar-btn" onClick={handleEditSelected}>{I.edit}Bearbeiten</button>
          ) : null}
          {canDelete && selectedIds.size >= 1 ? (
            <button className="tbar-del" onClick={handleDeleteSelected}>{I.trash}Löschen</button>
          ) : null}
        </>
      }
    />
  );

  // --- Mobile (Phone): Karten-Liste + Filter-Sheet (statt Tabelle) ---
  const mobileFilterBar = (
    <div className="ct-mobilebar">
      {arbeitsansichtBtn}
      <button
        type="button"
        className={"tbtn" + (activeFilterCount > 0 ? " primary" : "")}
        onClick={() => setFilterSheetOpen(true)}
      >
        {FunnelIcon}Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
      </button>
    </div>
  );

  // Eine Karte je Kontakt — in der Arbeitsansicht mit Zusatzzeile unter dem
  // Namen und T/S/Z-Badges, genau wie die Tabellenzeile.
  const kontaktKarte = (r: Row) => {
    const sel = selectedIds.has(r.kontaktId);
    const boName = r.boTeam?.name?.trim() || null;
    const verantw = boName
      ? boName.replace(/^Team\s+/i, "")
      : (r.foUsers?.length
          ? r.foUsers.map((u) => u.kuerzel || u.displayName).filter(Boolean).join(", ")
          : "") || r.foUser?.kuerzel || r.foUser?.displayName || "—";
    const anschrift = [r.ort, r.strasse].filter(Boolean).join(", ") || "—";
    return (
      <div className={"ct-card" + (sel ? " sel" : "")} onClick={() => openContact(r)} data-testid="ct-card">
        <div className="ct-card-head">
          <span className="typ">{r.typ === "Firma" ? FirmaIcon : PersonIcon}</span>
          <div className="ct-card-id">
            <div className={"ct-card-name" + (r.umfeldRolle?.ausgeschieden ? " kt-umfeld-aus-name" : "")}>{r.displayName || "—"}</div>
            <ArbeitsZusatzZeile zusatz={r.arbeitsZusatz} />
            <UmfeldRolleZeile rolle={r.umfeldRolle} />
            {r.kontaktStatus
              ? STATUS_MIT_EIGENEM_CHIP.includes(r.kontaktStatus)
                ? <Chip tone={STATUS_TONE[r.kontaktStatus] ?? "chip-sonstige"}>{r.kontaktStatus}</Chip>
                : <StatusPopover status={r.kontaktStatus} />
              : null}
          </div>
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={sel} onChange={() => toggle(r.kontaktId)} ariaLabel="Auswählen" />
          </span>
        </div>
        <div className="ct-card-body">
          <div className="ct-row"><span className="ct-k">Anschrift</span><span className="ct-v">{anschrift}</span></div>
          <div className="ct-row"><span className="ct-k">Verantwortlich</span><span className="ct-v">{verantw}</span></div>
          <div className="ct-row"><span className="ct-k">Relevanz</span><span className="ct-v">{r.kontaktStatus === "Interessent" && r.phase ? r.phase : <StarRating value={Number(r.relevanz ?? 0)} />}</span></div>
        </div>
        <div className="ct-card-foot" onClick={(e) => e.stopPropagation()}>
          <KontaktmoeglichkeitCell row={r} />
          <span className="ct-card-badges">
            {r.openEventTypes?.includes("V") ? (
              <button
                type="button"
                className="kt-vorgang-btn"
                title="Offene Vorgänge anzeigen"
                onClick={(e) => { e.stopPropagation(); setVorgaengeFor({ id: r.kontaktId, name: r.displayName ?? "" }); }}
              >
                <VorgangBadge title="Offene Vorgänge" />
              </button>
            ) : null}
            {r.nkgVorgangId ? (
              <button
                type="button"
                className="kt-vorgang-btn"
                title="NKG-Vorgang öffnen"
                onClick={(e) => { e.stopPropagation(); navigate(`/vorgaenge/${r.nkgVorgangId}`); }}
              >
                <span className="nkg-pill" title="NKG-Vorgang">NKG</span>
              </button>
            ) : null}
            <OffenePunkteBadges openEventTypes={r.openEventTypes} />
          </span>
        </div>
      </div>
    );
  };

  // Leerzustand: Steht ein Suchbegriff drin, bietet die Liste an, den Kontakt
  // daraus anzulegen („Kontakt nicht gefunden – neu anlegen?") — dieselben zwei
  // Wege wie in der globalen Suche (CommandPalette), mit vorbelegtem Namen.
  const suchbegriff = debouncedSearch.trim();
  // Erst „ohne Filter gäbe es N" — VOR dem Angebot, neu anzulegen: Wer einen
  // Kontakt nur wegen eines Filters nicht findet, soll keine Dublette anlegen.
  const leerText: React.ReactNode = loading
    ? "Lade Kontakte …"
    : ohneFilter.anzahl && ohneFilter.anzahl > 0
      ? (
        <OhneFilterHinweis
          anzahl={ohneFilter.anzahl}
          onOhneFilter={zeigeOhneFilter}
          text="Keine Treffer mit den gesetzten Filtern –"
          knopfText={(n) => `ohne Filter ${n} ${n === 1 ? "Ergebnis" : "Ergebnisse"} anzeigen`}
        />
      )
    : suchbegriff.length >= 2 && hasPermission("contacts:create")
      ? (
        <div className="ct-neu-aus-suche">
          <div>Kontakt nicht gefunden – neu anlegen?</div>
          <div className="ct-neu-aus-suche-knoepfe">
            <button type="button" className="tbtn" onClick={() => navigate(anlagePfadAusSuche("firma", suchbegriff))}>
              {I.plus}„{suchbegriff}“ als Firma
            </button>
            <button type="button" className="tbtn" onClick={() => navigate(anlagePfadAusSuche("privat", suchbegriff))}>
              {I.plus}„{suchbegriff}“ als Privatkontakt
            </button>
          </div>
        </div>
      )
      : "Keine Kontakte gefunden.";

  const cardList = (
    <div className="ct-cards" id="contacts-table-scroll" ref={scrollRef}>
      {abschnitte ? (
        // Arbeitsansicht: dieselben Abschnitte wie in der Tabelle.
        <KartenAbschnitte abschnitte={abschnitte} rowKey={(r) => r.kontaktId} karte={kontaktKarte} />
      ) : rows.length === 0 ? (
        <div className="empty">{leerText}</div>
      ) : (
        rows.map((r) => <React.Fragment key={r.kontaktId}>{kontaktKarte(r)}</React.Fragment>)
      )}
      {hasMore && (!arbeitsansicht || abschnitteOffen.all) ? (
        <button className="ct-more" onClick={() => loadMore()} disabled={loading}>
          {loading ? "Lädt …" : "Mehr laden"}
        </button>
      ) : null}
    </div>
  );

  const filterSheet = filterSheetOpen ? (
    <div className="ct-sheet-scrim" onClick={() => setFilterSheetOpen(false)}>
      <div className="ct-sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="ct-sheet-head">
          <h3>Filter</h3>
          <button className="dm-x" aria-label="Schließen" onClick={() => setFilterSheetOpen(false)}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
          </button>
        </div>
        <div className="ct-sheet-body">
          <div className="ct-sheet-fld">
            <span className="ct-sheet-lbl">Typ</span>
            {/* Am Telefon dieselbe Frage wie in der Tabellenspalte: In „CO
                intern" filtert „Typ" nach der Geschäftsbeziehung, sonst nach
                Firma/Person. Stünde hier Firma/Person, filterte das Blatt nach
                etwas anderem, als die Spalte darüber zeigt. */}
            {konf.typSpalteZeigtCoArt ? (
              <select
                className="sel"
                value={(filterValues[CO_ART_SPALTE] as string) ?? ""}
                onChange={(e) => updateFilter(CO_ART_SPALTE, e.target.value || undefined)}
              >
                <option value="">Alle</option>
                {CO_ARTEN.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            ) : (
              <select className="sel" value={(filterValues["Typ"] as string) ?? ""} onChange={(e) => updateFilter("Typ", e.target.value || undefined)}>
                <option value="">Alle</option>
                <option value="Firma">Firma</option>
                <option value="Person">Privat</option>
              </select>
            )}
          </div>
          <div className="ct-sheet-fld">
            <span className="ct-sheet-lbl">K-Status</span>
            {/* Dasselbe durchsuchbare Popup mit Rollen wie am Desktop. */}
            <KStatusSheetFeld
              value={filterValues["Kontaktstatus"] as string | undefined}
              onChange={(v) => updateFilter("Kontaktstatus", v)}
            />
          </div>
          <div className="ct-sheet-fld">
            <span className="ct-sheet-lbl">K-Möglichkeit</span>
            <input className="inp" placeholder="Telefon oder E-Mail …" value={(filterValues["kmoeglichkeit"] as string) ?? ""} onChange={(e) => updateFilter("kmoeglichkeit", e.target.value || undefined)} />
          </div>
          <div className="ct-sheet-fld">
            <span className="ct-sheet-lbl">Ort</span>
            <input className="inp" placeholder="Ort" value={(filterValues["Ort"] as string) ?? ""} onChange={(e) => updateFilter("Ort", e.target.value || undefined)} />
          </div>
          <div className="ct-sheet-fld">
            <span className="ct-sheet-lbl">Offene Vorgänge</span>
            {/* T-284: dieselbe Auswahl wie im Spaltenfilter — alle offenen oder je Art. */}
            <select
              className="sel"
              value={vorgangFilterWert(filterValues[VORGANG_SPALTE]) ?? ""}
              onChange={(e) => updateFilter(VORGANG_SPALTE, e.target.value || undefined)}
            >
              <option value="">Ohne Filter</option>
              {VORGANG_FILTER_OPTIONEN.map((o) => (
                <option key={o} value={o}>
                  {vorgangFilterEingerueckt(o) ? `\u00a0\u00a0– ${vorgangFilterLabel(o)}` : vorgangFilterLabel(o)}
                </option>
              ))}
            </select>
          </div>
          {teamOptions.length > 0 ? (
            <div className="ct-sheet-fld">
              <span className="ct-sheet-lbl">K-Verantwortlicher · Team</span>
              <div className="ct-sheet-checks">
                {teamOptions.map((o) => (
                  <label key={o.id} className="ct-sheet-check">
                    <input type="checkbox" checked={boFoValue.boTeamIds.includes(o.id)} onChange={() => toggleBoTeam(o.id)} />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          {userOptions.length > 0 ? (
            <div className="ct-sheet-fld">
              <span className="ct-sheet-lbl">K-Verantwortlicher · Person</span>
              <div className="ct-sheet-checks">
                {userOptions.map((o) => (
                  <label key={o.id} className="ct-sheet-check">
                    <input type="checkbox" checked={boFoValue.foUserIds.includes(o.id)} onChange={() => toggleFoUser(o.id)} />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <div className="ct-sheet-foot">
          <button className="tbar-btn" disabled={!weichtAb} onClick={clearAllFilters}>Zurücksetzen</button>
          <button className="cd-save" onClick={() => setFilterSheetOpen(false)}>Fertig</button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="kvp-rd kvp-rd-table-page">
      <div className="bar">
        <div className="search">
          {I.search}
          <input placeholder={konf.suchePlatzhalter} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {/* „Neu" führt direkt in die Anlage — Firma oder Privat wird dort nach
            dem Status gewählt (User 26.09.2026). */}
        <button type="button" className="tbtn primary" title={konf.anlageTitel} onClick={() => navigate("/contacts/new")}>
          {I.plus}Neu
        </button>
        <div className="spacer" style={{ flex: 1 }} />
        {konf.zeigtKopfAktionen && (
          <>
            <button className="tbtn" onClick={() => navigate("/datenbereinigung")}>{I.copy}Datenbereinigung</button>
            <button className="tbtn" onClick={() => navigate("/kontakte/papierkorb")}>{I.trash}Papierkorb</button>
          </>
        )}
        {autoErweitertFuer && (
          /* Der Umschalter springt sichtbar auf „Alle" — ohne ein Wort dazu
             sieht das wie ein Fehler aus. `role="status"` meldet es auch
             Screenreadern, ohne den Fokus zu stehlen. */
          <span className="kt-scope-auto" role="status">
            Wenige Treffer im Bereich — auf „Alle" erweitert.
          </span>
        )}
        <div className="rbac-seg" role="group" aria-label="Sichtbarkeitsbereich">
          {scopeOptions.map((o) => (
            <button
              key={o.value}
              type="button"
              className={"rbac-seg-btn" + (scope === o.value ? " on" : "")}
              aria-pressed={scope === o.value}
              onClick={() => scopeVonHandSetzen(o.value)}
            >
              <span className="rbac-dot" />
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {isPhone ? (
        <>
          {mobileFilterBar}
          {filterZeile}
          {cardList}
          {bottomBar}
        </>
      ) : (
        <>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.kontaktId}
        rowSelected={(r) => selectedIds.has(r.kontaktId)}
        onRowClick={openContact}
        emptyText={leerText}
        sort={sort}
        onSortChange={setSort}
        abschnitte={abschnitte}
        filterValues={filterValues}
        onFilterChange={updateFilter}
        boFoValue={boFoValue}
        onBoFoChange={setBoFoValue}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        columnWidths={columnWidths}
        onColumnWidthsChange={setColumnWidths}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={toggleCollapsed}
        onLoadMore={loadMore}
        hasMore={hasMore}
        loading={loading}
        scrollContainerId="contacts-table-scroll"
        scrollRef={scrollRef}
        aboveTable={tableSubBar}
        tableClassName={gridOn ? "kt-grid" : undefined}
      />

      {bottomBar}
        </>
      )}

      {filterSheet}

      <OffeneVorgaengeDialog
        open={!!vorgaengeFor}
        onOpenChange={(o) => {
          if (!o) setVorgaengeFor(null);
        }}
        contactId={vorgaengeFor?.id ?? null}
        contactName={vorgaengeFor?.name ?? ""}
      />
    </div>
  );
};

export default ContactTableRedesign;
